import { CardOffer } from '../../../../queries/pxtraCard/types';

type CardOfferInput = {
  cardEnabled: boolean;
  physicalCardEnabled: boolean;
  sachbezugSelected: boolean;
  /** Whether the company can offer the card on this set at all. */
  applies: boolean;
  baseline: CardOffer;
};

/** The offer the set should end up with. Where the card does not apply, the
 * baseline comes back so that nothing is written; a deselected Sachbezug still
 * takes the card away. */
export const deriveCardOffer = ({
  cardEnabled,
  physicalCardEnabled,
  sachbezugSelected,
  applies,
  baseline
}: CardOfferInput): CardOffer => {
  if (!applies) return baseline;

  const card = cardEnabled && sachbezugSelected;

  return { card_enabled: card, physical_card_enabled: card && physicalCardEnabled };
};

type CardOfferSeedInput = {
  benefitSetId: string;
  /** The set the store holds an offer for, if any. */
  seededFor: string | undefined;
  /** Whether that offer came from a set the new backend knew. */
  seededKnown: boolean;
  setKnown: boolean;
  cardFieldsChanged: boolean;
};

/** What a fetched offer does to the store: load fields and baseline, update
 * only the baseline under the user's edits, or nothing. A set seeded while
 * still unknown is seeded once more when it arrives. */
export const cardOfferSeed = ({
  benefitSetId,
  seededFor,
  seededKnown,
  setKnown,
  cardFieldsChanged
}: CardOfferSeedInput): 'load' | 'baseline' | 'skip' => {
  if (seededFor !== benefitSetId) return 'load';
  if (seededKnown || !setKnown) return 'skip';

  return cardFieldsChanged ? 'baseline' : 'load';
};

type SachbezugLockInput = {
  applies: boolean;
  editing: boolean;
  /** Whether the stored offer of the edited set has been loaded. */
  offerKnown: boolean;
  baseline: CardOffer;
  /** Whether the set has Sachbezug selected; adding it is never locked. */
  selected: boolean;
};

/** Whether Sachbezug must stay selected: the stored offer has the card, or it
 * is not known yet. The backend rejects a set that keeps the card without
 * Sachbezug, so removing the card is a save of its own. */
export const sachbezugLocked = ({
  applies,
  editing,
  offerKnown,
  baseline,
  selected
}: SachbezugLockInput): boolean =>
  applies && editing && selected && (!offerKnown || baseline.card_enabled);

/** A failed offer save. `permanent` errors are answers retrying cannot change,
 * such as a rejected request; the rest (network, server, a set the sync has
 * still not delivered) may pass. */
export type CardOfferSaveError = { code?: string; permanent: boolean };

export const cardOfferSaveError = (error: unknown): CardOfferSaveError => {
  if (typeof error !== 'object' || error === null) return { permanent: false };

  const { status, code } = error as { status?: unknown; code?: unknown };
  const knownCode = typeof code === 'string' && code ? code : undefined;
  const permanent =
    typeof status === 'number' &&
    status >= 400 &&
    status < 500 &&
    knownCode !== 'benefit_set_not_synced';

  return { code: knownCode, permanent };
};
